import React, { useState, useEffect } from "react";
import DataTable from "react-data-table-component";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { TendersService } from "../../../services/user/tenders.services"
import { handleNotify, TOASTER_POSITION, TOASTER_TYPE } from "../../common/notification/toaster_notify.component";
import DeleteModelPopupComponent from "../common_model_popup/deletemodelpopup.component";
import { handleCopy, handleExcel, handlePDF, handlePrint } from "../../../utils/filedownload";
import moment from "moment";

const TendersComponent = () => {
    const navigate = useNavigate();
    const selectedStore = useSelector(state => state?.user?.selectedStoreId);
    const [searchText, setSearchText] = useState("");
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [showModal, setShowModal] = useState(false);
    const [tenderToDelete, setTenderToDelete] = useState(null);
    const rowsPerPage = 5;

    // Fetch data
    useEffect(() => {
        const fetchTenderData = async () => {
            try {
                setLoading(true);
                const response = await TendersService.getAllTenders(selectedStore);
                setData(response);
            } catch (error) {
                handleNotify("Error fetching tenders", TOASTER_TYPE.ERROR, TOASTER_POSITION.TOP_RIGHT);
                setData([]);
            } finally {
                setLoading(false);
            }
        };
        fetchTenderData();
    }, [selectedStore]);

    // Handle pagination
    const indexOfLastRow = currentPage * rowsPerPage;
    const indexOfFirstRow = indexOfLastRow - rowsPerPage;

    const filteredData = data.filter(row =>
        Object.values(row).some(value =>
            value && value.toString().toLowerCase().includes(searchText.toLowerCase())
        )
    );

    const currentData = filteredData.slice(indexOfFirstRow, indexOfLastRow);
    const totalPages = Math.ceil(filteredData.length / rowsPerPage);

    const handlePageChange = (page) => {
        if (page < 1 || page > totalPages) return;
        setCurrentPage(page);
    };

    // Handle Edit
    const handleEditClick = (item) => {
        navigate('/tender/edit', { state: { tenderId: item?.tenderId } });
    };

    // Handle Delete
    const handleDeleteClick = (tender) => {
        setTenderToDelete(tender);
        setShowModal(true);
    };

    const handleDeleteConfirm = async () => {
        if (tenderToDelete) {
            try {
                await TendersService.deleteTender(tenderToDelete.tenderId);
                setData(prevData => prevData.filter(tender => tender.tenderId !== tenderToDelete.tenderId));
                handleNotify("Tender deleted successfully!", TOASTER_TYPE.SUCCESS, TOASTER_POSITION.TOP_RIGHT);
                setShowModal(false);
            } catch (error) {
                handleNotify(error.message || 'Error while deleting tender.', TOASTER_TYPE.ERROR, TOASTER_POSITION.TOP_RIGHT);
            }
        }
    };

    const handleAddNewClick = () => {
        navigate('/tender/create');
    };

    // Columns for DataTable
    const columns = [
        {
            name: "Tender No",
            selector: (row) => row.tenderNo,
            sortable: true,
            width: '15%',
        },
        {
            name: "Items Details",
            selector: (row) => row.itemDetails,
            sortable: true,
            width: '15%',
        },
        {
            name: "Model No",
            selector: (row) => row.modelNo,
            sortable: true,
            width: '15%',
        },
        {
            name: "Hospital Name",
            selector: (row) => row.hospitalName,
            sortable: true,
            width: '15%',
        },
        // {
        //     name: "Value",
        //     selector: (row) => row.value,
        //     sortable: true,
        //     width: '10%',
        // },
        {
            name: "Assigned to",
            selector: (row) => {
                return row.user?.username && row.user?.role?.roleName
                    ? `${row.user.username} (${row.user.role.roleName})`
                    : "";
            },
            sortable: true,
            width: '15%',
        },
        {
            width: '10%',
            name: 'Created',
            selector: row => row.sdReceivedDate || '',
            sortable: true
        },
        {
            width: '10%',
            name: 'Modified',
            selector: row => row.sdMatureDate || '',
            sortable: true
        },
        {
            name: "Actions",
            button: true,
            width: "15%",
            cell: (row) => (
                <div className="d-flex justify-content-center gap-2">
                    <button className="btn btn-primary" onClick={() => handleEditClick(row)}>
                        Edit
                    </button>
                    <button className="btn btn-danger" onClick={() => handleDeleteClick(row)}>
                        Delete
                    </button>
                </div>
            ),
        },
    ];

    return (
        <div className="page-wrapper m-0">
            <div className="page-content">
                <div className="page-breadcrumb d-flex align-items-center mb-3">
                    <div className="breadcrumb-title pe-3 text-primary fw-bold fs-4">
                        Tenders
                    </div>
                    <div className="ms-auto">
                        <button className="btn btn-primary" onClick={handleAddNewClick}>
                            New +
                        </button>
                    </div>
                </div>

                <div className="card radius-10 border-top border-0 border-4 border-primary p-4">
                    <div className="card-body">
                        <div className="d-flex flex-column flex-md-row mb-3">
                            <div className="btn-group mb-3 mb-md-0 me-2">
                                <button className="btn btn-outline-secondary" onClick={() => handleCopy(filteredData)}>Copy</button>
                                <button className="btn btn-outline-secondary" onClick={() => handleExcel(filteredData)}>Excel</button>
                                <button className="btn btn-outline-secondary" onClick={() => handlePDF(filteredData)}>PDF</button>
                                <button className="btn btn-outline-secondary" onClick={handlePrint}>Print</button>
                            </div>

                            <div className="d-flex ms-auto align-items-center">
                                <div className="me-2">
                                    <span className="fw-bold">Search:</span>
                                </div>
                                <input
                                    type="text"
                                    className="form-control w-100 w-md-auto"
                                    placeholder="Search 🔍..."
                                    value={searchText}
                                    onChange={(e) => setSearchText(e.target.value)}
                                />
                            </div>
                        </div>

                        <div className="table-responsive">
                            <DataTable
                                columns={columns}
                                data={currentData}
                                fixedHeader
                                responsive
                                highlightOnHover
                                sortIcon={<i className="fadeIn animated bx bx-sort" />}
                                progressPending={loading}
                            />
                        </div>

                        {showModal && (
                            <DeleteModelPopupComponent
                                show={showModal}
                                onClose={() => setShowModal(false)}
                                onConfirm={handleDeleteConfirm}
                                title="Confirm"
                                confirmText="Delete"
                                Message="Are you sure you want to delete this tender?"
                            />
                        )}

                        {/* Pagination Controls */}
                        <div className="d-flex mt-4 justify-content-end">
                            <div className="btn-group">
                                <button
                                    className="btn btn-outline-secondary"
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage === 1}
                                >
                                    Prev
                                </button>
                                {Array.from({ length: totalPages }, (_, index) => (
                                    <button
                                        key={index}
                                        className={`btn ${currentPage === index + 1 ? 'btn-primary' : 'btn-outline-secondary'}`}
                                        onClick={() => handlePageChange(index + 1)}
                                    >
                                        {index + 1}
                                    </button>
                                ))}
                                <button
                                    className="btn btn-outline-secondary"
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TendersComponent;
